import { onScopeDispose, ref, type Ref } from 'vue';
import Series from './types/Series';
import Study from './types/Study';
import CaseDef from './types/CaseDef';
import { seriesMatrixKey } from './display-sets';
import { cornerstone } from './cornerstone/cornerstone-setup.js';
import * as redactionStore from './redaction/redaction-store';
import type { RedactionRect } from './redaction/redaction-store';

// Re-exported for callers that still want the raw pixel math (the store owns it now).
export { applyRedactionToPixelData } from './redaction/redaction-store';

function getRectangleImageCoordinates(
  startHandle: { x: number; y: number },
  endHandle: { x: number; y: number }
): RedactionRect {
  return {
    left: Math.round(Math.min(startHandle.x, endHandle.x)),
    top: Math.round(Math.min(startHandle.y, endHandle.y)),
    width: Math.round(Math.abs(startHandle.x - endHandle.x)),
    height: Math.round(Math.abs(startHandle.y - endHandle.y)),
  };
}

/**
 * Drop every frame of a series from the image cache.
 *
 * This is a correctness requirement, not an optimization: frames cached from
 * before a redaction change still hold the old pixels, and `loadImage` would hand
 * them straight back. Evicting forces a re-decode, which runs the current
 * rectangles.
 */
function evictSeriesFrames(series: Series | null): void {
  for (const instance of series?.instances ?? []) {
    for (const frame of instance.frames) {
      try {
        cornerstone.imageCache.removeImageLoadObject(frame.imageId);
      } catch {
        // not cached — fine
      }
    }
  }
}

/**
 * Commit redaction rectangles for a series.
 *
 * Nothing is decoded and nothing is written to storage: the rectangles are the
 * redaction, and every decode path applies them (see redaction-store). Committing
 * used to walk every frame of every instance — loading it, saving a pre-redaction
 * copy, zeroing pixels, and writing the result back — which on a multi-thousand
 * frame cine meant minutes of work and gigabytes of I/O.
 */
export function updateRedaction(
  redactionBoxes: any[],
  selectedSeries: Series | null
): void {
  if (!selectedSeries) return;
  // An empty box list means the user backed out of the redaction tool ("Cancel
  // redaction"), not that they want existing redactions removed — that's what
  // removeRedactions is for.
  if (!redactionBoxes?.length) return;

  const rects: RedactionRect[] = redactionBoxes.map((box: any) =>
    getRectangleImageCoordinates(box.handles.start, box.handles.end)
  );

  redactionStore.setForInstances(
    selectedSeries.instances.map((instance) => instance.imageId),
    rects
  );
  // Remember the boxes so re-entering the redaction tool restores them for
  // adjustment, and so a second pass doesn't start from an empty list.
  selectedSeries.redactionBoxes = [...redactionBoxes];

  evictSeriesFrames(selectedSeries);
}

/**
 * Series in `study` that a redaction drawn on `series` can be applied to verbatim.
 *
 * The committed rectangle is absolute pixel coordinates, so "applies verbatim"
 * means one thing: the same image matrix. Ultrasound burns its PHI banner into a
 * fixed spot in the machine's screen layout, and that layout is what the matrix
 * size identifies — two 800x600 acquisitions from the same study put the banner in
 * the same place, and a 636x434 one does not.
 *
 * `series` itself is excluded, and a series whose matrix can't be established (an
 * unreadable header, or instances that disagree) never matches: "I could not prove
 * these are the same size" has to read as "do not apply".
 *
 * This deliberately does not try to spot rendered report/measurement screens,
 * which break the banner assumption despite matching the matrix. There is no
 * dependable header signal for them — a machine that exports them as US Image
 * Storage like everything else leaves no discriminator — so the guard is the
 * user's own review of each part, not a heuristic that would only look like one.
 */
export function seriesMatchingMatrix(study: Study, series: Series): Series[] {
  const key = seriesMatrixKey(series);
  if (!key) return [];
  return study.series.filter(
    (candidate) => candidate !== series && seriesMatrixKey(candidate) === key
  );
}

/** Geometry key for a box, so applying the same rectangle twice is idempotent. */
function boxKey(box: any): string {
  const { left, top, width, height } = getRectangleImageCoordinates(
    box.handles.start,
    box.handles.end
  );
  return `${left},${top},${width},${height}`;
}

/**
 * Copy a box so one drawn rectangle can be committed to several series without
 * them sharing an object. Cornerstone mutates handles in place as they're dragged,
 * so a shared box would move in every series at once.
 */
function cloneBox(box: any): any {
  return {
    ...box,
    handles: {
      ...box.handles,
      start: { ...box.handles.start },
      end: { ...box.handles.end },
      ...(box.handles.textBox ? { textBox: { ...box.handles.textBox } } : {}),
    },
  };
}

/**
 * Add the same rectangles to several series at once.
 *
 * Additive, not a replacement: a target series that already carries its own
 * redactions keeps them and gains these. `updateRedaction` overwrites — both the
 * store entry and `series.redactionBoxes` — which is right for the series being
 * edited, whose drawn set was restored from its own committed boxes on mount, but
 * would silently drop a target's existing boxes, un-redacting pixels the user had
 * already covered. Merging errs toward over-redacting, the safe direction.
 */
export function updateRedactionAcross(
  redactionBoxes: any[],
  seriesList: Series[]
): void {
  for (const series of seriesList) {
    const existing = Array.isArray(series.redactionBoxes)
      ? series.redactionBoxes
      : [];
    const alreadyThere = new Set(existing.map(boxKey));
    const added = redactionBoxes
      .filter((box) => !alreadyThere.has(boxKey(box)))
      .map(cloneBox);
    // Nothing new for this series — skip rather than re-commit, which would evict
    // its frames and force a pointless re-decode.
    if (added.length === 0) continue;
    updateRedaction([...existing, ...added], series);
  }
}

/**
 * Undo redaction for a series. Exact and always available — there's no stored copy
 * that could have been lost, only rectangles to delete.
 */
export function removeRedactions(selectedSeries: Series | null): void {
  if (!selectedSeries) return;

  redactionStore.clearForInstances(
    selectedSeries.instances.map((instance) => instance.imageId)
  );
  selectedSeries.redactionBoxes = [];

  evictSeriesFrames(selectedSeries);
}

/**
 * How many of a series' instances actually carry committed rectangles.
 *
 * Read straight from the store — the same map the export consults — rather than
 * from `series.redactionBoxes`, which is only the editor's handle state. Keeping
 * the UI on a second copy of the truth means a store miss (a key mismatch, say)
 * still shows as "redacted" in the interface while the exported pixels aren't.
 */
export function seriesRedactionStatus(series: Series): {
  redacted: number;
  total: number;
} {
  const total = series.instances.length;
  const redacted = series.instances.filter((instance) =>
    redactionStore.has(instance.imageId)
  ).length;
  return { redacted, total };
}

/** Case-wide tally for the pre-export review. Same source of truth as above. */
export function caseRedactionSummary(caseDef: CaseDef): {
  series: number;
  instances: number;
} {
  let series = 0;
  let instances = 0;
  for (const study of caseDef.studies) {
    for (const ser of study.series) {
      const { redacted } = seriesRedactionStatus(ser);
      if (redacted > 0) series += 1;
      instances += redacted;
    }
  }
  return { series, instances };
}

/**
 * A counter that ticks on every commit/clear, so a `computed` reading the store
 * (which is a plain Map, deliberately framework-free) re-evaluates when it changes.
 * Reference `version.value` inside the computed to register the dependency.
 */
export function useRedactionVersion(): Ref<number> {
  const version = ref(0);
  const unsubscribe = redactionStore.subscribe(() => {
    version.value += 1;
  });
  onScopeDispose(unsubscribe);
  return version;
}
